import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import SEO from './SEO';

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [checking, setChecking] = useState(true);
  const { updatePassword } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setHasSession(!!session);
      setChecking(false);
    };
    checkSession();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);

    try {
      await updatePassword(password);
      setSuccess(true);
      setTimeout(() => navigate('/models'), 2000);
    } catch (error) {
      setError(error.message || 'Failed to update password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-100">
        <p className="text-text-200">Verifying your reset link...</p>
      </div>
    );
  }

  if (!hasSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-100 py-12 px-4">
        <div className="max-w-md w-full text-center space-y-4">
          <img className="mx-auto h-10 w-auto" src="/logo_wordmark.svg" alt="SharpeMetrix" />
          <h2 className="text-xl font-bold text-text-100">Reset link expired or invalid</h2>
          <p className="text-text-200 text-sm">
            This password reset link has expired or is no longer valid. Please request a new one.
          </p>
          <a
            href="/forgot-password"
            className="inline-block py-2 px-4 text-sm font-medium rounded-md text-text-100 bg-primary-100 hover:bg-primary-200"
          >
            Request new reset link
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Reset Password"
        description="Set a new password for your SharpeMetrix account."
        url="/reset-password"
      />
      <div className="min-h-screen flex items-center justify-center bg-bg-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <img
              className="mx-auto h-10 w-auto"
              src="/logo_wordmark.svg"
              alt="SharpeMetrix"
            />
            <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-text-100">
              Set new password
            </h2>
          </div>

          {success ? (
            <div className="rounded-md bg-green-900/50 p-4">
              <div className="text-sm text-green-300 text-center">
                <p className="font-medium">Password updated successfully!</p>
                <p className="mt-1">Redirecting you to the app...</p>
              </div>
            </div>
          ) : (
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="password" className="block text-sm font-medium leading-6 text-text-100">
                  New Password
                </label>
                <div className="mt-2 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    className="block w-full rounded-md border-0 bg-bg-200 py-1.5 pr-10 text-text-100 shadow-sm ring-1 ring-inset ring-bg-300 placeholder:text-text-200 focus:ring-2 focus:ring-inset focus:ring-primary-100 sm:text-sm sm:leading-6"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-text-200 hover:text-text-100"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium leading-6 text-text-100">
                  Confirm New Password
                </label>
                <div className="mt-2">
                  <input
                    id="confirm-password"
                    name="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    className="block w-full rounded-md border-0 bg-bg-200 py-1.5 text-text-100 shadow-sm ring-1 ring-inset ring-bg-300 placeholder:text-text-200 focus:ring-2 focus:ring-inset focus:ring-primary-100 sm:text-sm sm:leading-6"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
              </div>

              {error && (
                <div className="text-red-500 text-sm text-center">{error}</div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-text-100 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-100"
                >
                  {loading ? 'Updating...' : 'Update password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
